import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import { sanitizeInput } from '../../utils/inputValidation';
import { getCesionesListing } from '../../services/cesionesService';
import Swal from 'sweetalert2';

const formatearFecha = (fecha) => {
  if (!fecha || fecha === 'N/A') return 'N/A';
  try {
    if (fecha.includes('/')) { const [d, m, y] = fecha.split('/'); return `${d}-${m}-${y}`; }
    if (fecha.includes('-')) { const p = fecha.split('-'); return p[0].length === 4 ? `${p[2]}-${p[1]}-${p[0]}` : fecha; }
    return fecha;
  } catch { return fecha; }
};

const RevisionCesion = ({ user, onLogout, onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [cesiones, setCesiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const enRevision = await getCesionesListing('EN REVISION', 0);
        if (enRevision.length === 0) {
          setCesiones([]);
          Swal.fire({ icon: 'info', title: 'Sin cesiones pendientes', text: 'No hay cesiones en revision en este momento.', confirmButtonColor: '#ef4444' });
          return;
        }
        const mapped = enRevision.map((item, idx) => ({
          id: item.cessionID || idx + 1,
          cessionID: item.cessionID || '',
          grupo: item.objContract?.group || 'N/A',
          orden: item.objContract?.order || 'N/A',
          comprador: item.objBuyer?.fullName || 'N/A',
          vendedor: item.objSeller?.fullName || 'N/A',
          personType: item.objBuyer?.personType || '',
          fechaEstado: formatearFecha(item.statusDate || ''),
          estado: item.cessionStatus || 'EN REVISION',
        }));
        setCesiones(mapped);
      } catch (err) {
        console.error('Error cargando cesiones EN REVISION:', err);
        setError(err.message || 'Error al cargar los datos');
        setCesiones([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const filteredCesiones = cesiones.filter(c => {
    const s = searchTerm.toLowerCase();
    return (
      String(c.grupo).toLowerCase().includes(s) ||
      String(c.orden).toLowerCase().includes(s) ||
      String(c.comprador).toLowerCase().includes(s) ||
      String(c.cessionID).toLowerCase().includes(s)
    );
  });

  const handleRevisar = useCallback((cesion) => {
    onNavigate('aprobacionCesion', {
      cessionID: cesion.cessionID,
      grupo: cesion.grupo !== 'N/A' ? cesion.grupo : '',
      orden: cesion.orden !== 'N/A' ? cesion.orden : '',
      estado: cesion.estado,
    });
  }, [onNavigate]);

  const handleVerDetalle = useCallback((cesion) => {
    onNavigate('visualizacionCesion', {
      cessionID: cesion.cessionID,
      grupo: cesion.grupo !== 'N/A' ? cesion.grupo : '',
      orden: cesion.orden !== 'N/A' ? cesion.orden : '',
      estado: cesion.estado,
    });
  }, [onNavigate]);

  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="max-w-full mx-auto px-3 py-4">

        {/* Titulo */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>
            <h2 className="text-2xl font-bold text-gradient">Revision de Cesiones</h2>
          </div>
          <p className="text-gray-500 text-sm ml-5">Cesiones pendientes de revision y aprobacion</p>
        </div>

        {/* Buscador */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Buscar</label>
              <div className="relative">
                <span className="material-icons absolute left-3 top-2.5 text-gray-400 text-sm">search</span>
                <input type="text" placeholder="Buscar por grupo, orden, comprador o Cession ID..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(sanitizeInput(e.target.value))}
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                />
              </div>
            </div>
            
             
          </div>
        </div>

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
            <span className="material-icons text-red-600">error_outline</span>
            <div>
              <p className="text-sm font-bold text-red-800">Error al cargar datos</p>
              <p className="text-xs text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Tabla */}
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12">
            <HondaLoader message="Cargando cesiones en revision..." variant="inline" size="large" subtitle="Obteniendo cesiones pendientes de aprobacion" />
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-red-600 to-red-700">
                  <tr>
                    {['Cession ID', 'Grupo', 'Orden', 'Comprador', 'Vendedor'].map(h => (
                      <th key={h} className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{h}</th>
                    ))}
                    <th className="px-4 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCesiones.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                        <span className="material-icons text-4xl text-gray-300 block mb-2">pending_actions</span>
                        {searchTerm ? 'No se encontraron resultados para la busqueda' : 'No hay cesiones en revision en este momento'}
                      </td>
                    </tr>
                  ) : filteredCesiones.map(cesion => (
                    <tr key={cesion.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">{cesion.cessionID}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{cesion.grupo}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{cesion.orden}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{cesion.comprador}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{cesion.vendedor}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center gap-2 justify-center">
                          {/* Ir a aprobar/rechazar */}
                          <button onClick={() => handleRevisar(cesion)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-green-600 hover:bg-green-600 hover:text-white transition-colors"
                            title="Revisar y aprobar"
                          >
                            <span className="material-icons text-xl">arrow_forward</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredCesiones.length > 0 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                Mostrando {filteredCesiones.length} de {cesiones.length} cesiones en revision
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default RevisionCesion;